import { z } from 'zod';

import {
  calendarDateSchema,
  colorSchema,
  idSchema,
  instantSchema,
  paginatedSchema,
} from './common';
import { projectHealthSchema, trashEntityTypeSchema } from './enums';
import { personSchema } from './identity';
import { LIMITS } from './limits';

/** Contracts of projects and groups (api-design §3.4, PRD §7–8). */

/** monday-style group colors offered in the color picker (GRP-1). */
export const GROUP_COLORS = [
  '#579bfc',
  '#00c875',
  '#a25ddc',
  '#fdab3d',
  '#e2445c',
  '#037f4c',
  '#0086c0',
  '#ff158a',
  '#784bd1',
  '#bb3354',
  '#ff642e',
  '#9cd326',
  '#cab641',
  '#7f5347',
  '#c4c4c4',
] as const;

const projectNameSchema = z
  .string()
  .trim()
  .min(1, 'Give the project a name.')
  .max(LIMITS.projectNameMax);
const descriptionSchema = z.string().trim().max(LIMITS.projectDescriptionMax);

export const projectPermissionsSchema = z.object({
  canManage: z.boolean(),
  canCreateTasks: z.boolean(),
});

/** A row of Projects Overview and the sidebar (PRJ-7, PRJ-8). */
export const projectSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  managerId: idSchema,
  startDate: calendarDateSchema.nullable(),
  endDate: calendarDateSchema.nullable(),
  health: projectHealthSchema,
  isPrivate: z.boolean(),
  /** D42: a demo project for training; goes with everything in it when the demo data is removed. */
  demo: z.boolean(),
  archivedAt: instantSchema.nullable(),
  /** Done top-level tasks ÷ all top-level tasks, rounded down (PRJ-4). */
  progress: z.number().int().min(0).max(100),
  openCount: z.number().int(),
  overdueCount: z.number().int(),
  lastActivityAt: instantSchema,
});

export const projectsResponseSchema = z.object({ items: z.array(projectSummarySchema) });

export const groupSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  name: z.string(),
  color: colorSchema,
  position: z.string(),
});

/** `GET /projects/{id}`: everything the project page header needs. */
export const projectDetailSchema = z.object({
  id: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  managerId: idSchema,
  startDate: calendarDateSchema.nullable(),
  endDate: calendarDateSchema.nullable(),
  health: projectHealthSchema,
  isPrivate: z.boolean(),
  /** D42: see the summary. */
  demo: z.boolean(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
  members: z.array(personSchema),
  groups: z.array(groupSchema),
  permissions: projectPermissionsSchema,
});

export const createProjectRequestSchema = z
  .object({
    name: projectNameSchema,
    description: descriptionSchema.optional(),
    managerId: idSchema.optional(),
    startDate: calendarDateSchema.nullable().optional(),
    endDate: calendarDateSchema.nullable().optional(),
    isPrivate: z.boolean().optional(),
    memberIds: z.array(idSchema).max(200).optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'The end date must be on or after the start date.',
  });

export const updateProjectRequestSchema = z
  .object({
    name: projectNameSchema.optional(),
    description: descriptionSchema.nullable().optional(),
    managerId: idSchema.optional(),
    startDate: calendarDateSchema.nullable().optional(),
    endDate: calendarDateSchema.nullable().optional(),
    health: projectHealthSchema.optional(),
    isPrivate: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field.' });

export const replaceMembersRequestSchema = z.object({
  memberIds: z.array(idSchema).max(200),
});

export const listProjectsQuerySchema = z.object({
  archived: z.stringbool().optional(),
});

export const createGroupRequestSchema = z.object({
  name: z.string().trim().min(1, 'Give the group a name.').max(LIMITS.groupNameMax),
  color: colorSchema.optional(),
});

export const updateGroupRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(LIMITS.groupNameMax).optional(),
    color: colorSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.color !== undefined, {
    message: 'Change at least one field.',
  });

/** Place an item right after `afterId` (null = first). The server computes the position. */
export const moveRequestSchema = z.object({ afterId: idSchema.nullable() });

export const deletionResponseSchema = z.object({ deletionId: idSchema });

export type ProjectSummary = z.infer<typeof projectSummarySchema>;
export type ProjectDetail = z.infer<typeof projectDetailSchema>;
export type Group = z.infer<typeof groupSchema>;
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

// ── Trash (TRS-1) ───────────────────────────────────────────────────────────────────────────────

export const trashEntrySchema = z.object({
  id: idSchema,
  entityType: trashEntityTypeSchema,
  /** Name at the time of deletion. */
  label: z.string(),
  projectId: idSchema.nullable(),
  projectName: z.string().nullable(),
  deletedBy: idSchema,
  deletedAt: instantSchema,
  /** Days until it is removed for good (TRS-3). */
  daysLeft: z.number().int(),
});

export const trashPageSchema = paginatedSchema(trashEntrySchema);

export type TrashEntry = z.infer<typeof trashEntrySchema>;
export type TrashPage = z.infer<typeof trashPageSchema>;
